import { NavigationButtons } from '@onegov/react-component-library';
import {
  Col,
  ErrorSummary,
  FormGroup,
  FormRadioGroup,
  Heading,
  HorizontalRule,
  InPageAlert,
  Input,
  Row,
} from '@snsw/react-component-library';
import { PlantItemRenewalContactDetailsPage } from 'plantItemRenewal/constants/plantItemRenewalContactDetailsPage';
import { storePlantItemRenewalApplicantContactDetails } from 'plantItemRenewal/redux/plantItemRenewalApplicantDetailsSlice';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { radioOptionsYesNo } from 'shared/constants/constants';
import { PagePath } from 'shared/constants/pagePath';
import { isEmpty, isValidEmail, scrollToError, validateExt } from 'shared/utils/Validation';
import { NewContactNumber } from 'shared/components/contactNumber/NewContactNumber';
import '../Page.module.scss';
import { PhoneNumber } from 'shared/utils/PhoneNumber';

export const PlantItemRenewalContactDetails = () => {
  const PAGE_CONSTANTS = PlantItemRenewalContactDetailsPage;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const loginEmail = useSelector((state) => state.passwordlessOidc?.user?.profile?.email);
  const storedApplicantDetails = useSelector((state) => state.plantItemRenewalApplicantDetails);
  const payment = useSelector((state) => state.plantItemRenewalPayment);

  const [changeEmail, setChangeEmail] = useState(storedApplicantDetails?.changeEmail || '');
  const [newEmailAddress, setNewEmailAddress] = useState(storedApplicantDetails?.newEmailAddress || '');
  const [phoneNumberRaw, setPhoneNumberRaw] = useState(storedApplicantDetails?.phoneNumber?.raw?.number || '');
  const [phoneNumber, setphoneNumber] = useState(storedApplicantDetails?.phoneNumber || '');
  const [errors, setErrors] = useState({});
  const [showErrors, setShowErrors] = useState(false);
  const [errorSummary, setErrorSummary] = useState([]);

  const onContinueClick = () => {
    const isValid = validateExt(validators, setErrors, setErrorSummary);
    setShowErrors(!isValid);

    if (isValid) {
      storeApplicantDetails();
      navigate(PagePath.plantItemRenewalDeclaration);
    } else {
      scrollToError();
    }
  };

  const storeApplicantDetails = () => {
    dispatch(
      storePlantItemRenewalApplicantContactDetails({
        emailAddress: loginEmail,
        changeEmail,
        newEmailAddress,
        phoneNumber: phoneNumber,
      }),
    );
  };

  const onBackClick = () => {
    navigate(PagePath.plantItemRenewalReviewItems);
  };

  const validators = [
    {
      fieldName: 'changeEmail',
      fieldValue: changeEmail,
      isValid: () => !!changeEmail,
      errorMessage: 'Please select an option',
    },
    {
      fieldName: 'newEmailAddress',
      fieldValue: newEmailAddress,
      isValid: (v) => changeEmail != 'yes' || isValidEmail(v),
      errorMessage: changeEmail ? 'Please enter email address' : 'Please enter valid email address',
    },
    {
      fieldName: 'phoneNumber',
      fieldValue: phoneNumberRaw,
      isValid: (v) => !isEmpty(v) && phoneNumber.isValid(),
      errorMessage: (v) => (isEmpty(v) ? 'Please enter phone number' : 'Please enter valid phone number'),
    },
  ];

  useEffect(() => {
    if (showErrors) validateExt(validators, setErrors, setErrorSummary);
  }, [changeEmail, newEmailAddress, phoneNumber]);

  useEffect(() => {
    setphoneNumber(new PhoneNumber('', phoneNumberRaw, undefined, true));
  }, [phoneNumberRaw]);

  return (
    <>
      <Row>
        <Col span={9}>{showErrors && !!errorSummary?.length && <ErrorSummary errors={errorSummary} />}</Col>
      </Row>

      <Row>
        <Col span={9}>
          <InPageAlert variant="info" title="Don't miss renewal reminders">
            <p>We need the best contact details for these plant items.</p>
          </InPageAlert>
        </Col>
      </Row>

      <Row>
        <Col span={9}>
          <Heading level={2} style={{ marginBottom: 0 }}>
            Email address
          </Heading>
          <p>
            The registration certificate(s) and payment receipt for this renewal will be sent to <b>{loginEmail}</b>
          </p>
          <FormRadioGroup
            id="changeEmail"
            name="changeEmail"
            vertical={true}
            options={radioOptionsYesNo}
            onChange={setChangeEmail}
            value={changeEmail}
            legend={PAGE_CONSTANTS.changeEmailLabel}
            helpMessage={PAGE_CONSTANTS.changeEmailHelpMessage}
            errorMessage={errors?.changeEmail?.errorMessage}
            hasError={showErrors && errors?.changeEmail?.invalid}
          />
          {changeEmail === 'yes' && (
            <>
              <InPageAlert
                variant="info"
                title="If your application includes someone else’s personal details, you need to get their permission before you supply it."
                style={{ marginTop: '2rem', marginBottom: '2rem' }}
              ></InPageAlert>

              <FormGroup
                id="newEmailAddress"
                label={PAGE_CONSTANTS.newEmailLabel}
                helpMessage={PAGE_CONSTANTS.newEmailHelpMessage}
                errorMessage={errors?.newEmailAddress?.errorMessage}
                hasError={showErrors && errors?.newEmailAddress?.invalid}
                margin={{ top: 'lg' }}
              >
                <Input value={newEmailAddress} onChange={(e) => setNewEmailAddress(e.target.value)} inputWidth="xl" />
              </FormGroup>
            </>
          )}
        </Col>
      </Row>

      <Row>
        <Col span={9}>
          <Heading level={2} style={{ marginBottom: 0 }}>
            Phone number
          </Heading>

          <p style={{ marginTop: 0 }}>Enter the best contact number for these registered plant items.</p>

          <NewContactNumber
            onChange={setPhoneNumberRaw}
            id="phoneNumber"
            value={phoneNumberRaw}
            label={PAGE_CONSTANTS.phoneNumberLabel}
            helpMessage={PAGE_CONSTANTS.phoneNumberHelpMessage}
            errorMessage={errors?.phoneNumber?.errorMessage}
            hasError={showErrors && errors?.phoneNumber?.invalid}
            allowNonNumeric={false}
            inputWidth={'lg'}
          />
        </Col>
      </Row>

      <HorizontalRule />
      <NavigationButtons
        leftButtonConfig={{
          label: 'Continue',
          onClick: onContinueClick,
          isVisible: !payment.paymentReference,
        }}
        rightButtonConfig={{
          isVisible: false,
        }}
        backButtonConfig={{
          onClick: onBackClick,
          isVisible: !payment.paymentReference,
        }}
      />
    </>
  );
};
